import { commandBrand, commandLogo } from "../branding/brand";
import { RoutinesNavTree } from "./RoutinesNavTree";
import { Fragment, useState, useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import {
  MessageSquare,
  Monitor,
  Menu,
  X,
  Wifi,
  WifiOff,
  ChevronsLeft,
  ChevronsRight,
  Bot,
  Settings,
  Shield,
  ChevronRight,
  ExternalLink,
  Globe,
  AlertTriangle,
  Receipt,
  Store,
  Home,
  Activity,
  Users,
  Plug,
  CreditCard,
  CheckSquare,
  Terminal,
  FileCheck2,
  Sparkles,
  Play,
  SlidersVertical,
  ChevronDown,
  LayoutGrid,
  Database,
  AppWindow,
} from "lucide-react";
import {
  Button,
  Separator,
  Badge,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Toaster,
} from "@cloch-ai/ui";
import { useGatewayStore } from "../stores/gateway-store";
import { api } from "../lib/api";
import { resolveMeAuthIssues, type MeAuthStatus } from "../lib/auth-status";
import { OrgSelector } from "./OrgSelector";
import { RoleSelector } from "./RoleSelector";
import { ThemeToggle } from "./ThemeToggle";
import { isClerkConfigured, useCurrentUser } from "../providers/ClerkProvider";
import { useOrgContext, useOrgContextKnown } from "../providers/OrgProvider";
import { useApprovalsCount } from "../hooks/useApprovalsCount";
import { useIsOrgAdmin } from "../hooks/useIsOrgAdmin";
import { useIsPlatformStaff } from "../hooks/useIsPlatformStaff";
import { useUnifiedShell } from "../hooks/useUnifiedShell";
import { useUnifiedCatalog } from "../hooks/useUnifiedCatalog";
import { cn } from "../lib/utils";
import { AuthIssueActions } from "./AuthIssueActions";
import { OrgSetupBanner } from "./OrgSetupBanner";
import { OrgJoinChoice } from "./OrgJoinChoice";
import { BusinessNamePrompt } from "./BusinessNamePrompt";
import { isSynapsedxConsoleEnabled } from "../lib/synapsedx-console";
import { useConnections } from "../hooks/useConnections";
import { APPS_CHANGED_EVENT } from "../lib/apps-changed";
import { databaseConnectionsApi, type DatabaseConnection } from "../lib/database-connections";
import { toAppRows } from "../pages/connections/app-rows";
import { MENU_SCOPE } from "./menu-scope";

const SIDEBAR_COLLAPSED_KEY = "agents-sidebar-collapsed";
/**
 * Persisted open/closed intent for the Workspace nav group (SYN-2104, catalog
 * IA addendum §2). Collapsed by default — absent/invalid storage reads as
 * closed, matching SIDEBAR_COLLAPSED_KEY's fail-closed-to-default pattern.
 */
const WORKSPACE_NAV_OPEN_KEY = "agents-workspace-nav-open";

export type NavItem = {
  label: string;
  icon: typeof MessageSquare;
  href: string;
};

/**
 * Randy's door (SYN-2207, story 005.044 AC1/AC6).
 *
 * 🔴 DEFINED ONCE AND SPREAD INTO EVERY MEMBER-FACING NAV TABLE BELOW.
 * There are THREE of them — legacy `NAV_ITEMS`, and the two unified member
 * tables — and **cloch renders the legacy one**. Adding this to the unified
 * tables only would leave the deployed box with no door at all, while tsc,
 * the tests and the bundle all stayed green. That is the same trap that cost
 * two bugs on `routes.tsx`, where the fix was `databaseConnectionRoutes` spread into
 * both tables. `Layout.apps-nav.test.tsx` enumerates the tables rather than
 * naming one, so a fourth table added later fails the test instead of
 * silently dropping him.
 *
 * 🔧 SYN-2378, SUPERSEDING 005.044 AC5: RANDY GETS THE WORD "APPS".
 *
 * The 29 Jul resolution was "Connections" — the unqualified word for the
 * non-technical user, chosen because it stretches where "systems" does not:
 * his till is a system, his email and his files are not, and all three are
 * connections. That reasoning was sound and is NOT what changed.
 *
 * What changed is whose word it is. On 8 Aug the customer-facing noun was
 * decided by RANDY, and his reason is the value: "Apps" is what his assistant
 * can work with. It is a mental model rather than a nav preference — SMBs
 * already say "my apps". epic-006 is explicit that this RENAMES the existing
 * surface rather than adding a fifth noun, which is why the constant changes
 * here and no new entry appears anywhere.
 *
 * The admin console keeps "Org connections" for the reason it always had: it
 * is org-wide connector administration, and the qualifier belongs on the
 * specialist surface rather than on his.
 *
 * 🔧 SYN-2865, 4 Sep 2026: THE SURFACE NOW MEANS WHAT THE WORD SAYS.
 *
 * The rename shipped and the surface behind it did not widen with it.
 * `/connections` asked one source and rendered `{database} · {schema}`, so the
 * email this comment names as the reason for the word was not filtered out of
 * the list — it was UNREPRESENTABLE. Maria connected Microsoft 365, with a live
 * token and an active connection, and said *"i don't see m365 in the list of
 * apps"*.
 *
 * The href is unchanged, deliberately. Repointing it at `/workspace/connections`
 * was the obvious move and is wrong: that handler answers
 * `403 connections_admin_required` to a non-admin before it composes anything,
 * so Randy's employee would swap an incomplete list for a locked door — and D-2
 * says a non-admin may connect and disconnect their own credential. The page
 * behind this href learned the other kinds instead; see
 * `pages/connections/app-rows.ts`.
 */
export const RANDY_NAV_ITEM: NavItem = {
  label: "Apps",
  icon: Database,
  href: "/connections",
};

export const NAV_ITEMS: NavItem[] = [
  { label: "Chat", icon: MessageSquare, href: "/chat" },
  { label: "Routines", icon: Play, href: "/routines" },
  RANDY_NAV_ITEM,
  { label: "Agents", icon: Bot, href: "/agents" },
  { label: "Marketplace", icon: Store, href: "/marketplace" },
  { label: "FinOps", icon: Receipt, href: "/finops" },
  { label: "Settings", icon: Settings, href: "/settings" },
];

/**
 * Unified shell — member ("you") navigation, organized by job not engine
 * (SYN-1503, spec §6.1, SYN-1898). Buyer-language labels only.
 * Flag OFF: Home, Chat, Apps, Routines, Activity, My Setup.
 * Flag ON (ui.unified_catalog): Home, Chat, Mine, Global Marketplace, Activity,
 * My Setup. Routines remains visible for saved workflows (SYN-3126).
 *
 * 🔧 SYN-2378: the routines entry is labelled "Routines", matching both the
 * route it has always pointed at (`/routines`) and the mockup Randy was shown.
 * Only the label ever said "Run", so the label was the thing that was wrong.
 */
export const UNIFIED_MEMBER_NAV_BASE: NavItem[] = [
  { label: "Home", icon: Home, href: "/home" },
  { label: "Chat", icon: MessageSquare, href: "/chat" },
  RANDY_NAV_ITEM,
  { label: "Routines", icon: Play, href: "/routines" },
  { label: "Activity", icon: Activity, href: "/activity" },
  { label: "My Setup", icon: SlidersVertical, href: "/my-setup" },
];

/** Catalog-era member nav. Saved routines retain their own tree (SYN-3126). */
export const UNIFIED_MEMBER_NAV_CATALOG: NavItem[] = [
  { label: "Home", icon: Home, href: "/home" },
  { label: "Chat", icon: MessageSquare, href: "/chat" },
  RANDY_NAV_ITEM,
  { label: "Mine", icon: LayoutGrid, href: "/catalog" },
  { label: "Routines", icon: Play, href: "/routines" },
  { label: "Global Marketplace", icon: Store, href: "/catalog?scope=org" },
  { label: "Activity", icon: Activity, href: "/activity" },
  { label: "My Setup", icon: SlidersVertical, href: "/my-setup" },
];

/**
 * The simple menu (SYN-3668, Maria, 29 Oct 2026): Home, Chat, Apps and Routines, shown to
 * members and admins alike while `MENU_SCOPE` is "simple". The decision lives in
 * `./menu-scope`; the two tables above stay as the full menu it switches back to.
 */
export const SIMPLE_MEMBER_NAV: NavItem[] = [
  { label: "Home", icon: Home, href: "/home" },
  { label: "Chat", icon: MessageSquare, href: "/chat" },
  RANDY_NAV_ITEM,
  { label: "Routines", icon: Play, href: "/routines" },
];

/**
 * Unified shell — Workspace ("admin") navigation, shown only to org
 * admins/owners inside a collapsible group (spec §6.1, SYN-1898).
 * Approvals carries the pending count badge (SYN-1521).
 * "Agents" entry renamed to "Org Agents" to deduplicate the label used on
 * the member-nav "My Agents" entry (SYN-1898 AC4).
 */
export const UNIFIED_WORKSPACE_NAV: NavItem[] = [
  { label: "People", icon: Users, href: "/workspace/people" },
  { label: "Roles", icon: Shield, href: "/workspace/roles" },
  { label: "Org connections", icon: Plug, href: "/workspace/connections" },
  { label: "Skills", icon: Sparkles, href: "/workspace/skills" },
  { label: "Org apps", icon: AppWindow, href: "/workspace/apps" },
  { label: "Org Agents", icon: Bot, href: "/workspace/agents" },
  { label: "Governance", icon: FileCheck2, href: "/workspace/governance" },
  { label: "Billing", icon: CreditCard, href: "/workspace/billing" },
  { label: "Approvals", icon: CheckSquare, href: "/workspace/approvals" },
];

const SYNAPSEDX_STAFF_NAV: NavItem[] = [
  { label: "Setup Claude Code", icon: Terminal, href: "/setup/claude-code" },
  // synapsedx.console.enabled (VITE_SYNAPSEDX_CONSOLE, SYN-1953): the catalog
  // nav entry is only shown when the console flag is ON. The staff-shell gate
  // already requires useIsPlatformStaff; the DATA gate on the page is the
  // server grant (isPlatformAdmin, SYN-1939) — not this nav item.
  ...(isSynapsedxConsoleEnabled()
    ? [{ label: "Universe", icon: LayoutGrid, href: "/synapsedx/catalog" }]
    : []),
];

/**
 * Page title mapping
 */
const PAGE_TITLES: Record<string, string> = {
  "/home": "Home",
  "/chat": "Chat",
  "/customize": "Customize",
  "/my-setup": "My Setup",
  "/agents": "My Agents",
  "/catalog": "Mine",
  "/routines": "Run",
  "/run": "Run",
  "/activity": "Activity",
  "/marketplace": "Marketplace",
  "/finops": "FinOps",
  "/settings": "Settings",
  "/admin": "Admin",
  "/workspace": "Workspace",
  "/workspace/people": "People",
  "/workspace/roles": "Roles",
  "/workspace/skills": "Skills",
  "/workspace/apps": "Org apps",
  "/workspace/connections": "Org connections",
  // Randy's surface (SYN-2207). He holds the unqualified word; the admin console
  // above carries the "Org" qualifier (005.044 AC5, resolved 29 Jul).
  //
  // 🔧 SYN-2865: DERIVED from the nav item, not typed again. This said "Connections"
  // while the nav said "Apps", so clicking "Apps" landed on a page whose chrome named
  // something else — a second noun for one thing, which is the shape of the complaint
  // this ticket came from. One home for the word (principle 7); a rename cannot drift.
  "/connections": RANDY_NAV_ITEM.label,
  "/workspace/agents": "Org Agents",
  "/workspace/governance": "Governance",
  "/workspace/billing": "Billing",
  "/workspace/approvals": "Approvals",
  "/setup/claude-code": "Setup Claude Code",
  "/synapsedx/catalog": "Universe",
  "/onboarding": "Get started",
};

/** Resolve a location pathname to its nav identity under the unified shell.
 * SYN-1901: /my-setup is now a real page, so no backing-route aliases remain.
 * This is a passthrough kept for future alias needs without a contract break.
 */
function resolveNavPathname(pathname: string, _unifiedShell: boolean): string {
  return pathname;
}

export function getPageTitle(pathname: string): string {
  // Exact match
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }

  // Prefix match
  const prefix = Object.keys(PAGE_TITLES).find(
    (key) => key !== "/" && pathname.startsWith(key + "/"),
  );
  if (prefix) {
    return PAGE_TITLES[prefix];
  }

  // Fallback - capitalize first segment
  const segment = pathname.split("/").filter(Boolean)[0];
  if (segment) {
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  }

  return "Command";
}

/**
 * SynapseDx Neural Network Logo SVG - matches Atlas exactly
 */
function SynapseDxOrbSvg({ id, className }: { id: string; className?: string }) {
  return (
    <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <defs>
        <linearGradient id={`synapseGradient${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#00D4E6", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "#0099CC", stopOpacity: 1 }} />
        </linearGradient>
        <mask id={`networkMask${id}`}>
          <rect width="400" height="400" fill="black" />
          <g stroke="white" strokeWidth="10" strokeLinecap="round" fill="none">
            <line x1="200" y1="200" x2="120" y2="100" />
            <line x1="200" y1="200" x2="280" y2="150" />
            <line x1="200" y1="200" x2="80" y2="230" />
            <line x1="200" y1="200" x2="280" y2="250" />
            <line x1="200" y1="200" x2="150" y2="310" />
          </g>
          <g fill="white">
            <circle cx="200" cy="200" r="40" />
            <circle cx="120" cy="100" r="30" />
            <circle cx="280" cy="150" r="25" />
            <circle cx="80" cy="230" r="35" />
            <circle cx="280" cy="250" r="22" />
            <circle cx="150" cy="310" r="42" />
          </g>
        </mask>
      </defs>
      <rect
        width="400"
        height="400"
        fill={`url(#synapseGradient${id})`}
        mask={`url(#networkMask${id})`}
      />
    </svg>
  );
}

/**
 * Command Logo - SynapseDx orb + "Command" text
 */
function CommandLogo({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex items-center gap-2" title={commandBrand.body}>
      <div className="relative flex-shrink-0 h-7 w-7">
        {commandLogo ? (
          <img
            src={commandLogo.src}
            alt={commandLogo.alt}
            className="h-full w-full object-contain"
          />
        ) : (
          <SynapseDxOrbSvg id="command-logo" className="h-full w-full" />
        )}
      </div>
      {!collapsed && (
        <span className="font-bold text-base tracking-tight">
          <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-blue-600 bg-clip-text text-transparent">
            {commandBrand.siteName}
          </span>
          <span className="text-muted-foreground ml-1.5 font-medium">{commandBrand.headline}</span>
        </span>
      )}
    </div>
  );
}

/** Sites in the SynapseDx network */
const SYNAPSEDX_SITES = [
  { name: "Command", href: "/", domain: "command.synapsedx.co.uk", isCurrent: true },
  { name: "Atlas", href: "https://atlas.synapsedx.co.uk", domain: "atlas.synapsedx.co.uk" },
  { name: "ISAAC", href: "https://isaac.synapsedx.co.uk", domain: "isaac.synapsedx.co.uk" },
  {
    name: "Coherence",
    href: "https://coherence.synapsedx.co.uk",
    domain: "coherence.synapsedx.co.uk",
  },
  { name: "Memory", href: "https://memory.synapsedx.co.uk", domain: "memory.synapsedx.co.uk" },
  { name: "SynapseDx", href: "https://synapsedx.co.uk", domain: "synapsedx.co.uk" },
];

/**
 * Local sites menu using the SynapseDx orb as trigger icon.
 * Replaces the package SynapseSitesMenu which hardcodes a Globe icon.
 */
function CommandSitesMenu({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center w-full gap-2 rounded-lg px-3 py-1.5 text-sm",
            "bg-muted/30 text-foreground hover:bg-muted/50",
            "data-[state=open]:bg-muted/60 group",
            isCollapsed && "justify-center px-2",
          )}
          aria-label="SynapseDx sites"
          title={isCollapsed ? "SynapseDx sites" : undefined}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-md">
            <Globe className="h-4 w-4" />
          </span>
          {!isCollapsed && (
            <span className="flex flex-1 items-center text-xs font-semibold whitespace-nowrap">
              SynapseDx Sites
            </span>
          )}
          {!isCollapsed && (
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:translate-x-0.5" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={10}
        className="z-50 w-72 rounded-xl border bg-card p-2 shadow-xl"
      >
        {/* Header with orb icon */}
        <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md">
            <SynapseDxOrbSvg id="sites-header" className="h-7 w-7" />
          </div>
          <span className="text-sm font-semibold">SynapseDx Network</span>
        </div>
        <DropdownMenuSeparator className="my-2" />
        {/* Site links */}
        {SYNAPSEDX_SITES.map((site) => (
          <DropdownMenuItem key={site.name} asChild>
            <a
              href={site.href}
              target={site.isCurrent ? undefined : "_blank"}
              rel={site.isCurrent ? undefined : "noopener noreferrer"}
              className="group flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted/50 focus:bg-muted/50"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-gradient-to-br from-cyan-400 via-blue-400 to-blue-600" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">{site.name}</span>
                <span className="truncate text-xs text-muted-foreground">{site.domain}</span>
              </span>
              <span className="ml-auto flex items-center gap-2">
                {site.isCurrent && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Current
                  </span>
                )}
                {!site.isCurrent && (
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                )}
              </span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Sidebar nav-section renderer shared by the unified shell (SYN-1503). Renders
 * a list of {@link NavItem}s with the same active/collapsed treatment as the
 * legacy nav so flag-on chrome reads identically to flag-off chrome.
 */
/**
 * The connections THIS PERSON set up, by the names they gave them, beneath "Apps" in the sidebar
 * (Maria, 4 Oct 2026; the rule and what it excludes are written at `isOwnConnectionInstance`).
 *
 * Maria asked for this repeatedly. Each time, the work landed on the Apps PAGE — the
 * expandable rows in `pages/connections/index.tsx` — because that is where the word
 * "tree" already lived. The menu could never have shown one: `NavItem` is
 * `{label, icon, href}` and both render paths map an item to a single `Link`. Nothing
 * in the sidebar has ever had children.
 *
 * 🔑 THE ROWS COME FROM `toAppRows`, the same function the Apps page uses, so the menu
 * and the page can never disagree about what an app is or what it is called. A second
 * derivation here would be a second home for the same fact, and it would drift the first
 * time either surface learned about a new kind.
 *
 * ⚠️ WHAT THIS DOES NOT SHOW, stated rather than left to be discovered: the PARTS inside
 * an app. `toAppRows` fills `children` from a card's sub-bundles, and the card for a real
 * customer connection is built with `subBundles: []` — Coherence's readiness payload
 * carries no bundle data per held connection, so Command has nothing to populate it from.
 * That is a separate defect. Until it is fixed, the second level would be empty for every
 * real connection, so this renders one level and says so.
 */
type AppsNavTreeProps = { isCollapsed: boolean; onNavigate: () => void };

/**
 * Mounted only once an organisation is known (see `useOrgContextKnown`): its two reads 403 for
 * a brand-new person until then, and the tree is not keyed on the org, so it would keep the
 * empty list the 403 left it.
 */
function AppsNavTree(props: AppsNavTreeProps) {
  return useOrgContextKnown() ? <AppsNavTreeForOrg {...props} /> : null;
}

function AppsNavTreeForOrg({ isCollapsed, onNavigate }: AppsNavTreeProps) {
  const { connections, refetch } = useConnections();
  const [databases, setDatabases] = useState<DatabaseConnection[]>([]);
  // Held in a ref so the listeners below are attached once, whatever identity `refetch` has.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    // Unmount guard: navigating away mid-fetch left a promise writing into a dead
    // component on the Apps page, and this is the same call.
    let live = true;
    // Each read is numbered so an older answer that lands late never overwrites a newer one.
    let latest = 0;
    const listDatabases = () => {
      const mine = ++latest;
      databaseConnectionsApi
        .list()
        .then((result) => {
          if (live && mine === latest) setDatabases(result.connections);
        })
        .catch(() => {
          // ⚠️ A NARROWING, NOT A FAILURE, and named as one. A database read that fails
          // leaves the connector apps listed and the databases missing — a shorter list
          // that still looks complete. The Apps page is the surface that reports the
          // error; the menu must not grow an error state of its own.
          if (live && mine === latest) setDatabases([]);
        });
    };
    listDatabases();
    // 🔴 cloch, 6 Oct 2026: Xero connected in chat at 09:58Z, and the tree stayed stale until
    // Maria reloaded the page — it read its lists once, on mount. It re-reads BOTH lists when a
    // connection is made, revived, reconnected or removed anywhere on the page
    // (lib/apps-changed.ts; `useConnections` re-reads the connector list on the same signal),
    // and when she comes back to the tab, which is how a sign-in finished in another tab returns.
    const onAppsChanged = () => listDatabases();
    const onReturn = () => {
      listDatabases();
      void refetchRef.current();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") onReturn();
    };
    window.addEventListener(APPS_CHANGED_EVENT, onAppsChanged);
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      window.removeEventListener(APPS_CHANGED_EVENT, onAppsChanged);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const rows = toAppRows({ databases, connectors: connections });

  // Nothing to show is not a tree with no items — it is no tree. A collapsed rail has
  // no room for labels, and an org holding nothing gets the plain "Apps" link it had.
  if (isCollapsed || rows.length === 0) {
    return null;
  }

  return (
    <ul
      data-testid="apps-nav-tree"
      className="mt-0.5 mb-1 space-y-0.5 border-l border-border/60 ml-6 pl-3"
    >
      {rows.map((row) => (
        <li key={row.id}>
          <Link
            to={RANDY_NAV_ITEM.href}
            onClick={onNavigate}
            title={row.needsAttention ? `${row.label}: ${row.statusLabel}` : row.label}
            className="block truncate rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {row.label}
            {/* A broken app of hers is listed WITH its state: never hidden, never shown as
                healthy. A working one says nothing extra; the menu has no room for "Connected". */}
            {row.needsAttention ? (
              <span
                data-testid="apps-nav-tree-state"
                className="text-amber-600 dark:text-amber-400"
              >
                {` · ${row.statusLabel}`}
              </span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SidebarNavSection({
  items,
  isCollapsed,
  isActive,
  onNavigate,
  badges,
}: {
  items: NavItem[];
  isCollapsed: boolean;
  isActive: (href: string) => boolean;
  onNavigate: () => void;
  /** Pending-count badges by href (SYN-1521: the Approvals inbox count). */
  badges?: Record<string, number | null>;
}) {
  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.href);
        const badgeCount = badges?.[item.href] ?? null;
        const showBadge = typeof badgeCount === "number" && badgeCount > 0;
        const link = (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            className={cn(
              "relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isCollapsed ? "justify-center px-2" : "",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
            title={isCollapsed ? item.label : undefined}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span className="truncate">{item.label}</span>}
            {showBadge && (
              <span
                data-testid={`nav-badge-${item.href}`}
                aria-label={`${badgeCount} waiting`}
                className={cn(
                  "flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-none text-primary-foreground",
                  isCollapsed
                    ? "absolute right-0.5 top-0.5 h-4 min-w-4 px-1 text-[10px]"
                    : "ml-auto",
                )}
              >
                {badgeCount}
              </span>
            )}
          </Link>
        );
        // The tree hangs off Apps in BOTH render paths. Only one of them is the path
        // cloch renders (the legacy one below), and a tree added to one alone is the
        // shape that has already cost this epic two bugs.
        return item.href === RANDY_NAV_ITEM.href ? (
          <Fragment key={item.href}>
            {link}
            <AppsNavTree isCollapsed={isCollapsed} onNavigate={onNavigate} />
          </Fragment>
        ) : item.href === "/routines" ? (
          <Fragment key={item.href}>
            {link}
            <RoutinesNavTree isCollapsed={isCollapsed} onNavigate={onNavigate} />
          </Fragment>
        ) : (
          link
        );
      })}
    </>
  );
}

/**
 * Unified-shell navigation (SYN-1503, spec §6.1, SYN-1898). Member nav for
 * everyone; the Workspace section appears below a divider only for org
 * admins/owners, inside a collapsible group (SYN-1898 AC3). The platform-staff
 * Sites menu (gated by {@link useIsPlatformStaff}) and the connection status /
 * user footer are rendered by the surrounding aside.
 */
function UnifiedNav({
  isCollapsed,
  isOrgAdmin,
  isPlatformStaff,
  isActive,
  onNavigate,
  unifiedCatalog,
}: {
  isCollapsed: boolean;
  isOrgAdmin: boolean;
  isPlatformStaff: boolean;
  isActive: (href: string) => boolean;
  onNavigate: () => void;
  /** When true, the member nav shows Mine and Global Marketplace instead of Run. */
  unifiedCatalog: boolean;
}) {
  // Admin-only fetch: members never request the approvals count (the nav item
  // doesn't exist for them either — their view is Home "Waiting on you").
  // SYN-3668: the simple menu hides the Workspace group from admins too, so its count is
  // not fetched either.
  const fullMenu = MENU_SCOPE === "full";
  const showWorkspace = isOrgAdmin && fullMenu;
  const approvalsCount = useApprovalsCount(showWorkspace);
  // The user's explicit open/closed intent for the Workspace group — collapsed
  // by default, persisted to localStorage (SYN-2104, catalog IA addendum §2).
  // Kept independent of the sidebar's collapse state (SYN-1898): a collapsed
  // sidebar renders the icon rail regardless, and expanding the sidebar
  // restores this prior intent instead of forcing the group open or shut.
  const [workspaceOpen, setWorkspaceOpen] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return localStorage.getItem(WORKSPACE_NAV_OPEN_KEY) === "true";
  });
  useEffect(() => {
    localStorage.setItem(WORKSPACE_NAV_OPEN_KEY, String(workspaceOpen));
  }, [workspaceOpen]);
  // When the whole sidebar is collapsed there's no expander; the icon rail
  // always shows. When expanded, honour the user's intent.
  const showWorkspaceItems = isCollapsed || workspaceOpen;

  const memberNav = !fullMenu
    ? SIMPLE_MEMBER_NAV
    : unifiedCatalog
      ? UNIFIED_MEMBER_NAV_CATALOG
      : UNIFIED_MEMBER_NAV_BASE;

  return (
    <nav className="flex-1 overflow-auto p-3 space-y-1">
      <SidebarNavSection
        items={memberNav}
        isCollapsed={isCollapsed}
        isActive={isActive}
        onNavigate={onNavigate}
      />
      {showWorkspace && (
        <>
          <Separator className="my-2" />
          {!isCollapsed ? (
            <button
              type="button"
              aria-expanded={workspaceOpen}
              aria-controls="workspace-nav-group"
              onClick={() => setWorkspaceOpen((v) => !v)}
              className="flex w-full items-center justify-between px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              <span>Workspace</span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform duration-200",
                  workspaceOpen ? "rotate-0" : "-rotate-90",
                )}
              />
            </button>
          ) : null}
          {showWorkspaceItems && (
            <div id="workspace-nav-group">
              <SidebarNavSection
                items={UNIFIED_WORKSPACE_NAV}
                isCollapsed={isCollapsed}
                isActive={isActive}
                onNavigate={onNavigate}
                badges={{ "/workspace/approvals": approvalsCount }}
              />
            </div>
          )}
        </>
      )}
      {isPlatformStaff && (
        <>
          <Separator className="my-2" />
          {!isCollapsed && (
            <p className="px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              SynapseDx
            </p>
          )}
          <SidebarNavSection
            items={SYNAPSEDX_STAFF_NAV}
            isCollapsed={isCollapsed}
            isActive={isActive}
            onNavigate={onNavigate}
          />
        </>
      )}
    </nav>
  );
}

function ConnectionStatus({ collapsed }: { collapsed: boolean }) {
  const status = useGatewayStore((s) => s.status);
  const isConnected = status === "connected";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center gap-2 px-3 py-2">
            {isConnected ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-500" />
            )}
            {!collapsed && (
              <Badge variant={isConnected ? "default" : "destructive"}>{status}</Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Gateway {status}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return stored === "true";
    }
    return false;
  });
  const location = useLocation();
  const { fullName, email, isSignedIn } = useCurrentUser();
  const { currentOrg, orgSetupError, orgJoinOffers, orgJoinRequests, businessNameRequest } =
    useOrgContext();
  const orgContextKnown = useOrgContextKnown();
  const isPlatformStaff = useIsPlatformStaff();
  const isOrgAdmin = useIsOrgAdmin();
  const unifiedShell = useUnifiedShell();
  const unifiedCatalog = useUnifiedCatalog();
  const gatewayStatus = useGatewayStore((s) => s.status);
  // Map backing routes (e.g. /customize → /my-setup) to their nav identity so
  // the unified-shell rename survives navigation (SYN-1898).
  const navPathname = resolveNavPathname(location.pathname, unifiedShell);
  const pageTitle = getPageTitle(navPathname);
  const [hadSignedInSession, setHadSignedInSession] = useState(false);
  const [authStatus, setAuthStatus] = useState<MeAuthStatus | null>(null);
  const isSuperAdmin = authStatus?.session.isPlatformAdmin === true;

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    if (isClerkConfigured() && isSignedIn) {
      setHadSignedInSession(true);
    }
  }, [isSignedIn]);

  async function refreshAuthStatus(): Promise<void> {
    if (!isSignedIn || !currentOrg?.orgId) {
      setAuthStatus(null);
      return;
    }
    try {
      const data = await api.get<MeAuthStatus>("/api/me/auth-status");
      setAuthStatus(data);
    } catch {
      setAuthStatus(null);
    }
  }

  useEffect(() => {
    if (!isSignedIn || !currentOrg?.orgId) {
      setAuthStatus(null);
      return;
    }
    let cancelled = false;
    void api
      .get<MeAuthStatus>("/api/me/auth-status")
      .then((data) => {
        if (!cancelled) {
          setAuthStatus(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAuthStatus(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, currentOrg?.orgId]);

  const showSessionExpiredBanner = isClerkConfigured() && hadSignedInSession && !isSignedIn;
  const showGatewayReconnectBanner =
    isClerkConfigured() && isSignedIn && gatewayStatus !== "connected";
  const authIssues = authStatus
    ? resolveMeAuthIssues(authStatus, { viewerIsAdmin: isSuperAdmin })
    : [];
  const authBannerIssue = authIssues[0] ?? null;
  const authBanner = authBannerIssue
    ? {
        tone: authBannerIssue.severity,
        message: authBannerIssue.summary,
        actions: authBannerIssue.actions,
      }
    : null;

  const isActive = (href: string): boolean => {
    const [hrefPath, hrefQuery = ""] = href.split("?");
    if (hrefQuery) {
      return navPathname === hrefPath && location.search === `?${hrefQuery}`;
    }
    if (hrefPath === "/catalog") {
      return (
        navPathname === "/catalog" && new URLSearchParams(location.search).get("scope") !== "org"
      );
    }
    return navPathname === hrefPath || navPathname.startsWith(`${hrefPath}/`);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-card border-r flex flex-col transition-all duration-300 ease-in-out lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "w-16" : "w-60",
        )}
      >
        {/* Logo Section - h-14 matches header height */}
        <div
          className={cn(
            "flex items-center h-14 px-3 border-b",
            isCollapsed ? "justify-center" : "",
          )}
        >
          <Link to="/chat" className="flex items-center">
            <CommandLogo collapsed={isCollapsed} />
          </Link>
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden ml-auto"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation. Flag ON: jobs-based unified shell (SYN-1503, §6.1).
            Flag OFF: today's IA, rendered byte-identically.
            When ui.unified_catalog is also on, the member nav swaps
            "Run" for Mine + Global Marketplace (SYN-2066). */}
        {unifiedShell ? (
          <UnifiedNav
            isCollapsed={isCollapsed}
            isOrgAdmin={isOrgAdmin}
            isPlatformStaff={isPlatformStaff}
            isActive={isActive}
            onNavigate={() => setSidebarOpen(false)}
            unifiedCatalog={unifiedCatalog}
          />
        ) : (
          <>
            <nav className="flex-1 overflow-auto p-3 space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                const link = (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isCollapsed ? "justify-center px-2" : "",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                    title={isCollapsed ? item.label : undefined}
                    aria-label={item.label}
                    aria-current={active ? "page" : undefined}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
                // 🔴 THIS is the path the deployed cloch box renders. A tree added only
                // to `UnifiedNav` above would leave the box Maria looks at unchanged
                // while tsc, the unit suite and the bundle all stayed green.
                return item.href === RANDY_NAV_ITEM.href ? (
                  <Fragment key={item.href}>
                    {link}
                    <AppsNavTree
                      isCollapsed={isCollapsed}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  </Fragment>
                ) : item.href === "/routines" ? (
                  <Fragment key={item.href}>
                    {link}
                    <RoutinesNavTree
                      isCollapsed={isCollapsed}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  </Fragment>
                ) : (
                  link
                );
              })}
              {isPlatformStaff && (
                <>
                  <Separator className="my-2" />
                  {!isCollapsed && (
                    <p className="px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      SynapseDx
                    </p>
                  )}
                  <SidebarNavSection
                    items={SYNAPSEDX_STAFF_NAV}
                    isCollapsed={isCollapsed}
                    isActive={isActive}
                    onNavigate={() => setSidebarOpen(false)}
                  />
                </>
              )}
            </nav>

            {/* Admin link - only visible to org admins/owners */}
            {isOrgAdmin && (
              <div className="px-3 pb-2">
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isCollapsed ? "justify-center px-2" : "",
                    location.pathname.startsWith("/admin")
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                  title={isCollapsed ? "Admin" : undefined}
                  aria-label="Admin"
                  aria-current={location.pathname.startsWith("/admin") ? "page" : undefined}
                >
                  <Shield className="h-5 w-5 shrink-0" />
                  {!isCollapsed && <span className="truncate">Admin</span>}
                </Link>
              </div>
            )}
          </>
        )}

        <Separator />

        {/* Connection status */}
        <ConnectionStatus collapsed={isCollapsed} />

        {/* SynapseDx app switcher — engine links are internal-only, rendered
            solely for platform staff (SynapseDx Clerk org members, SYN-1504) */}
        {isPlatformStaff && (
          <div className="px-3 py-2">
            <CommandSitesMenu isCollapsed={isCollapsed} />
          </div>
        )}

        <Separator />

        {/* Footer: User + Collapse Toggle */}
        <div className={cn("border-t p-2", isCollapsed ? "space-y-2" : "")}>
          {isCollapsed ? (
            <>
              <div className="flex justify-center py-2">
                {isClerkConfigured() && isSignedIn ? (
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox: "h-8 w-8",
                      },
                    }}
                  />
                ) : (
                  <Link
                    to="/sign-in"
                    className="flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                    title="Sign in"
                  >
                    <Monitor className="h-5 w-5" />
                  </Link>
                )}
              </div>
              <button
                type="button"
                className="flex w-full items-center justify-center rounded-lg py-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setIsCollapsed(false)}
                title="Expand sidebar"
                aria-label="Expand sidebar"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
              {isClerkConfigured() && isSignedIn ? (
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "h-8 w-8",
                    },
                  }}
                />
              ) : (
                <Link
                  to="/sign-in"
                  className="flex items-center gap-3 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <Monitor className="h-5 w-5" />
                  <span>Sign in</span>
                </Link>
              )}
              {isClerkConfigured() && isSignedIn && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">
                    {fullName || email || "User"}
                  </p>
                </div>
              )}
              <button
                type="button"
                className="h-8 w-8 shrink-0 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setIsCollapsed(true)}
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header bar - h-14 aligns with sidebar logo section */}
        <header className="h-14 border-b bg-card flex items-center justify-between px-6">
          {/* Mobile menu button + Page title */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{pageTitle}</h1>
          </div>

          {/* Right: Org Selector + Role Selector + Theme Toggle */}
          <div className="flex items-center gap-2">
            <OrgSelector compact />
            <span className="text-muted-foreground">|</span>
            <RoleSelector compact />
            <div className="ml-2">
              <ThemeToggle />
            </div>
          </div>
        </header>

        {(showSessionExpiredBanner || showGatewayReconnectBanner || authBanner) && (
          <div
            className={cn(
              "border-b px-6 py-2 text-sm flex items-center justify-between gap-3",
              showSessionExpiredBanner
                ? "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400"
                : showGatewayReconnectBanner
                  ? "bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400"
                  : authBanner?.tone === "error"
                    ? "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400"
                    : "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400",
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {showSessionExpiredBanner
                  ? "Session expired. Sign in again to resume durable chat operations."
                  : showGatewayReconnectBanner
                    ? `Gateway reconnecting (${gatewayStatus}). Messages will resume when connected.`
                    : authBanner?.message}
              </span>
            </div>
            {showSessionExpiredBanner ? (
              <Link
                to="/sign-in"
                className="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium hover:bg-background/70"
              >
                Sign in
              </Link>
            ) : authBanner ? (
              <div className="shrink-0">
                <AuthIssueActions
                  actions={authBanner.actions}
                  onRefresh={refreshAuthStatus}
                  maxActions={2}
                />
              </div>
            ) : null}
          </div>
        )}

        {/*
         * The cold start that did not complete (SYN-2491). Separate from the strip above
         * rather than folded into its ternary chain: this one can be true at the same time as
         * a gateway reconnect, and it is the more actionable of the two — a user with no
         * workspace has nothing to reconnect TO.
         */}
        <OrgSetupBanner />

        {/*
         * The workspace choice (SYN-2876). Above the page content, like the banner, because
         * a person with no workspace 403s on everything below it — but this one is a
         * QUESTION, not a failure, and it must never be answered on their behalf.
         */}
        <OrgJoinChoice />
        <BusinessNamePrompt />

        {/* Page content — key on orgId forces remount on org switch so all
           data hooks refetch for the new organization (AC-005 / RISK-004).
           Chat page gets full-bleed treatment (no padding/overflow) since
           CommandCenterLayout manages its own panels and scrolling. */}
        <main
          key={currentOrg?.orgId ?? ""}
          className={cn(
            "flex-1",
            location.pathname === "/chat" ? "overflow-hidden" : "overflow-auto p-6",
          )}
        >
          {orgContextKnown ? (
            children
          ) : orgSetupError ||
            businessNameRequest ||
            orgJoinOffers.length > 0 ||
            orgJoinRequests.length > 0 ? null : (
            /*
             * A brand-new person's workspace is being created (OrgProvider's cold start). Nothing
             * that reads is mounted until it exists: every such read is a certain 403. When the
             * banner or a question above is showing, it is the message, so this stays empty.
             */
            <p role="status" className="p-6 text-sm text-muted-foreground">
              Setting up your workspace…
            </p>
          )}
        </main>
      </div>

      {/* Toast notifications (SYN-1508): run confirmations ("Running Invoice
          chase...") and plain-language run refusals. Mounted once at the shell
          so toasts survive the navigation into the chat thread. */}
      <Toaster position="bottom-right" />
    </div>
  );
}
